import type { Availability } from '@otryad/shared';
import {
  deleteCloudStorageItem,
  getCloudStorageItem,
  setCloudStorageItem,
} from '@telegram-apps/sdk-react';
import { useCallback, useSyncExternalStore } from 'react';

/**
 * Черновик онбординга (BLUEPRINT Stage 3, задача 1): хранится в Telegram CloudStorage, чтобы не
 * терять ответы при закрытии Mini App; вне Telegram (dev) и на старых клиентах — в localStorage.
 * Хранилище — маленький external store: экраны читают через `useOnboardingDraft`.
 */
export interface OnboardingDraft {
  cityId?: string;
  districtIds: string[];
  availability?: Availability;
  primaryActivityId?: string | null;
  squadSize?: number | null;
  /** activityId → уровень 1–5 */
  levels: Record<string, number>;
  interestActivityIds: string[];
  birthYear?: number;
  phone?: string;
  /** Дополнительные наборы: активность → размер отряда. Лимит очередей — на сервере. */
  extraChoices: Record<string, number | null>;
  agreedRules: boolean;
  agreedPrivacy: boolean;
  /** Пользователь предложил активность и решил ждать решения без выбора основной */
  waitingForProposal: boolean;
  /** Приглашение запасного (deep link `reserve_<token>`): принять после онбординга (Stage 6) */
  reserveToken?: string;
  /** Последний завершённый шаг (1–5) */
  step: number;
  updatedAt: number;
}

export const DRAFT_KEY = 'otryad.onboarding.draft.v1';
const DRAFT_TTL_MS = 30 * 24 * 3_600_000;
/** CloudStorage отвечает через хост Telegram; если ответа нет — работаем с localStorage. */
const CLOUD_TIMEOUT_MS = 2_000;

export const emptyDraft = (): OnboardingDraft => ({
  districtIds: [],
  levels: {},
  interestActivityIds: [],
  extraChoices: {},
  agreedRules: false,
  agreedPrivacy: false,
  waitingForProposal: false,
  step: 0,
  updatedAt: 0,
});

export function parseDraft(
  raw: string | null | undefined,
  now = Date.now(),
): OnboardingDraft | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<OnboardingDraft>;
    if (!parsed || typeof parsed !== 'object') return null;
    if (typeof parsed.updatedAt !== 'number' || now - parsed.updatedAt > DRAFT_TTL_MS) return null;
    return { ...emptyDraft(), ...parsed };
  } catch {
    return null;
  }
}

interface Storage {
  get(): Promise<string | null>;
  set(value: string): Promise<void>;
  remove(): Promise<void>;
}

function local(): Storage {
  return {
    async get() {
      try {
        return window.localStorage.getItem(DRAFT_KEY);
      } catch {
        return null;
      }
    },
    async set(value) {
      try {
        window.localStorage.setItem(DRAFT_KEY, value);
      } catch {
        /* приватный режим — черновик живёт только в памяти */
      }
    },
    async remove() {
      try {
        window.localStorage.removeItem(DRAFT_KEY);
      } catch {
        /* ignore */
      }
    },
  };
}

function cloud(): Storage {
  const fallback = local();
  return {
    async get() {
      if (!getCloudStorageItem.isAvailable()) return fallback.get();
      try {
        const v = await getCloudStorageItem(DRAFT_KEY, { timeout: CLOUD_TIMEOUT_MS });
        return v || (await fallback.get());
      } catch {
        return fallback.get();
      }
    },
    async set(value) {
      await fallback.set(value);
      if (setCloudStorageItem.isAvailable()) {
        try {
          await setCloudStorageItem(DRAFT_KEY, value, { timeout: CLOUD_TIMEOUT_MS });
        } catch {
          /* CloudStorage недоступен — остаётся localStorage */
        }
      }
    },
    async remove() {
      await fallback.remove();
      if (deleteCloudStorageItem.isAvailable()) {
        try {
          await deleteCloudStorageItem(DRAFT_KEY, { timeout: CLOUD_TIMEOUT_MS });
        } catch {
          /* ignore */
        }
      }
    },
  };
}

type Listener = () => void;

class DraftStore {
  private draft: OnboardingDraft = emptyDraft();
  private listeners = new Set<Listener>();
  private storage: Storage;
  private loaded: Promise<void> | null = null;
  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  /** true — из хранилища восстановлен непустой черновик */
  restored = false;

  constructor(storage: Storage) {
    this.storage = storage;
  }

  /** Загружает черновик один раз; повторные вызовы возвращают тот же промис. */
  load(): Promise<void> {
    if (!this.loaded) {
      this.loaded = this.storage
        .get()
        .then((raw) => {
          const parsed = parseDraft(raw);
          if (parsed) {
            this.draft = parsed;
            this.restored = parsed.step > 0;
            this.emit();
          }
        })
        .catch(() => undefined);
    }
    return this.loaded;
  }

  get(): OnboardingDraft {
    return this.draft;
  }

  update(patch: Partial<OnboardingDraft>): void {
    this.draft = { ...this.draft, ...patch, updatedAt: Date.now() };
    this.emit();
    this.scheduleSave();
  }

  async clear(): Promise<void> {
    this.draft = emptyDraft();
    this.restored = false;
    this.emit();
    if (this.saveTimer) clearTimeout(this.saveTimer);
    await this.storage.remove();
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit() {
    for (const l of this.listeners) l();
  }

  private scheduleSave() {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      void this.storage.set(JSON.stringify(this.draft));
    }, 300);
  }
}

export const draftStore = new DraftStore(typeof window === 'undefined' ? local() : cloud());

export function useOnboardingDraft() {
  const draft = useSyncExternalStore(
    (cb) => draftStore.subscribe(cb),
    () => draftStore.get(),
    () => draftStore.get(),
  );
  const update = useCallback((patch: Partial<OnboardingDraft>) => draftStore.update(patch), []);
  return { draft, update, clear: () => draftStore.clear(), load: () => draftStore.load() };
}

/** Экран, с которого продолжать по последнему завершённому шагу. */
export const ONBOARDING_PATHS = [
  '/onboarding/city',
  '/onboarding/district',
  '/onboarding/schedule',
  '/onboarding/activities',
  '/onboarding/about',
] as const;

export function resumePath(step: number): string {
  const idx = Math.min(Math.max(step, 0), ONBOARDING_PATHS.length - 1);
  return ONBOARDING_PATHS[idx] ?? ONBOARDING_PATHS[0];
}
